import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit as fsLimit,
  Timestamp,
  getDoc,
  setDoc,
  writeBatch
} from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { getCurrentTenantId } from './tenantContext';
import { classService as classServiceV2 } from './classServiceV2';
import { studentService as studentServiceV2 } from './studentServiceV2';
import { sessionService as sessionServiceV2 } from './sessionServiceV2';
import { attendanceService as attendanceServiceV2 } from './attendanceServiceV2';
import { validateWirdSave } from './wirdRules';
import { listWirdRatingsByStudent } from './wirdRatingsService';

const AUTH_BACKEND = process.env.REACT_APP_AUTH_BACKEND || 'firebase';

// Session type constants — Moroccan msid pedagogy (الجديد / الماضي / السرد).
// Internal keys kept as SABAK / SABAQ_PARA / DHOR for Firestore compatibility;
// user-facing labels are Al-Jadīd / Al-Māḍī / As-Sard.
export const SESSION_TYPES = {
  SABAK: 'SABAK',          // Al-Jadīd — new daily lesson (was "NEW")
  SABAQ_PARA: 'SABAQ_PARA', // Al-Māḍī — short-term revision (was "TIKRAR")
  DHOR: 'DHOR'             // As-Sard — long-term cumulative recitation (was "REVISION")
};

// Map legacy activityType (Phase 1 schema) to new session type (Phase 2 schema)
export const mapLegacyActivityType = (activityType) => {
  switch (activityType) {
    case 'NEW': return SESSION_TYPES.SABAK;
    case 'REVISION': return SESSION_TYPES.DHOR;
    case 'TIKRAR': return SESSION_TYPES.SABAQ_PARA;
    default: return activityType;
  }
};

// Reverse mapping (so historical dailyLogs can be surfaced alongside new sessions)
export const mapSessionTypeToLegacy = (sessionType) => {
  switch (sessionType) {
    case SESSION_TYPES.SABAK: return 'NEW';
    case SESSION_TYPES.DHOR: return 'REVISION';
    case SESSION_TYPES.SABAQ_PARA: return 'TIKRAR';
    default: return sessionType;
  }
};

// Classes Service
const classServiceFirebase = {
  async createClass(classData) {
    try {
      const tid = getCurrentTenantId();
      const docRef = await addDoc(collection(db, 'classes'), {
        ...classData,
        tenantId: classData.tenantId || tid || null,
        createdAt: Timestamp.now()
      });
      return docRef.id;
    } catch (error) {
      throw new Error('Error creating class: ' + error.message);
    }
  },

  async getClasses() {
    const tid = getCurrentTenantId();
    if (!tid) {
      // User has no tenant attached — return empty rather than throwing
      // a permissions error. Upstream UI shows a friendly prompt.
      console.warn('getClasses: no tenant context, returning empty list');
      return [];
    }
    const q = query(
      collection(db, 'classes'),
      where('tenantId', '==', tid),
      orderBy('createdAt', 'desc'),
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  },

  async updateClass(classId, classData) {
    try {
      await updateDoc(doc(db, 'classes', classId), {
        ...classData,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      throw new Error('Error updating class: ' + error.message);
    }
  },

  async deleteClass(classId) {
    try {
      await deleteDoc(doc(db, 'classes', classId));
    } catch (error) {
      throw new Error('Error deleting class: ' + error.message);
    }
  }
};

// Phase 2.3 selector — switches at module load.
export const classService = AUTH_BACKEND === 'api' ? classServiceV2 : classServiceFirebase;

// Students Service
const studentServiceFirebase = {
  async createStudent(studentData) {
    try {
      const tid = getCurrentTenantId();
      const memorizedPortions = studentData.memorizedPortions || [];
      const docRef = await addDoc(collection(db, 'students'), {
        ...studentData,
        tenantId: studentData.tenantId || tid || null,
        memorizedPortions,
        // Snapshot taken at enrolment: the hifz the student brought with
        // them. This list is NEVER extended — anything learned inside the
        // school lands on `memorizedPortions` only. Used to distinguish
        // pre-joining hifz from at-school progress.
        priorMemorizedPortions:
          studentData.priorMemorizedPortions || [...memorizedPortions],
        // Subset of `memorizedPortions` promoted via approved
        // auto-suggestions (memorizationSuggestionsService). Everything
        // in memorizedPortions that isn't in this list or in
        // priorMemorizedPortions is implicitly teacher-taught.
        autoMemorizedPortions: studentData.autoMemorizedPortions || [],
        isActive: true,
        createdAt: Timestamp.now()
      });
      return docRef.id;
    } catch (error) {
      throw new Error('Error creating student: ' + error.message);
    }
  },

  async getStudents(classId = null) {
    const tid = getCurrentTenantId();
    if (!tid) {
      console.warn('getStudents: no tenant context, returning empty list');
      return [];
    }
    const filters = [
      where('isActive', '==', true),
      where('tenantId', '==', tid),
    ];
    if (classId) filters.push(where('assignedClass', '==', classId));
    const q = query(
      collection(db, 'students'),
      ...filters,
      orderBy('studentFullName'),
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  },

  /**
   * Phone signup auto-link (PLAN1 §J.1 / parent onboarding).
   * Given a phone number (E.164 format), find every active student row
   * that has it on `fatherPhoneNumber`. Returns the students grouped by
   * tenant so the caller can bind the parent account. Unauthenticated
   * client use is blocked by rules; a freshly phone-verified user can
   * read students where `fatherPhoneNumber == request.auth.token.phone_number`.
   */
  async findByFatherPhone(phone) {
    if (!phone) return { tenantId: null, studentIds: [], students: [] };
    try {
      const q = query(
        collection(db, 'students'),
        where('fatherPhoneNumber', '==', phone),
        where('isActive', '==', true),
      );
      const snap = await getDocs(q);
      if (snap.empty) return { tenantId: null, studentIds: [], students: [] };
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // If the phone is on students from multiple tenants (rare, e.g.
      // the same father has kids in two madaris on this platform), we
      // pick the first tenant deterministically and require the admin
      // of the second one to link them manually.
      const tenantId = rows[0].tenantId;
      const sameTenant = rows.filter((r) => r.tenantId === tenantId);
      return {
        tenantId,
        studentIds: sameTenant.map((r) => r.id),
        students: sameTenant,
      };
    } catch (error) {
      console.warn('findByFatherPhone failed:', error.message);
      return { tenantId: null, studentIds: [], students: [] };
    }
  },

  async getStudentById(studentId) {
    try {
      const docRef = doc(db, 'students', studentId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      } else {
        throw new Error('Student not found');
      }
    } catch (error) {
      throw new Error('Error fetching student: ' + error.message);
    }
  },

  async updateStudent(studentId, studentData) {
    try {
      await updateDoc(doc(db, 'students', studentId), {
        ...studentData,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      throw new Error('Error updating student: ' + error.message);
    }
  },

  async archiveStudent(studentId) {
    try {
      await updateDoc(doc(db, 'students', studentId), {
        isActive: false,
        archivedAt: Timestamp.now()
      });
    } catch (error) {
      throw new Error('Error archiving student: ' + error.message);
    }
  },

  async unarchiveStudent(studentId) {
    try {
      await updateDoc(doc(db, 'students', studentId), {
        isActive: true,
        archivedAt: null,
        restoredAt: Timestamp.now()
      });
    } catch (error) {
      throw new Error('Error restoring student: ' + error.message);
    }
  },

  async getArchivedStudents() {
    const tid = getCurrentTenantId();
    if (!tid) {
      console.warn('getArchivedStudents: no tenant context, returning empty list');
      return [];
    }
    const q = query(
      collection(db, 'students'),
      where('isActive', '==', false),
      where('tenantId', '==', tid),
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }))
      .sort((a, b) => {
        const at = a.archivedAt?.toMillis?.() || 0;
        const bt = b.archivedAt?.toMillis?.() || 0;
        return bt - at;
      });
  },

  /**
   * Irreversible hard-delete of a student and every tenant-scoped record
   * that references them. Used from the "المحذوفون" recovery view after
   * an explicit double-confirmation — once called, nothing can be
   * recovered from the client.
   *
   * Notes on what does and does not get wiped:
   *   - students, dailyLogs, sessions, attendance, reviewQueue,
   *     quizAttempts, reciteAttempts, tadabburEntries — fully deleted.
   *   - The student's own `users/{uid}` doc is deleted. The parent's
   *     `users/{uid}` doc is deleted ONLY if no other active student
   *     in the tenant still lists the same `fatherPhoneNumber` (so we
   *     don't orphan a parent who has siblings in the madrasa).
   *   - wirdRatings, certificates, notifications, conversation messages
   *     are append-only by Firestore rules (see firestore.rules) — they
   *     cannot be deleted from the client and will remain as an audit
   *     trail. This is by design, not an oversight.
   *   - Firebase Auth accounts for the student/parent cannot be deleted
   *     from the client SDK either; the doc-level deletion is what locks
   *     them out of the app.
   */
  async permanentlyDeleteStudent(studentId) {
    if (!studentId) throw new Error('studentId is required');
    const tid = getCurrentTenantId();

    let studentData = null;
    try {
      const snap = await getDoc(doc(db, 'students', studentId));
      if (snap.exists()) studentData = snap.data();
    } catch (_) {
      // If we can't read the student doc we still try the best-effort wipe.
    }

    const RELATED_COLLECTIONS = [
      'dailyLogs',
      'sessions',
      'attendance',
      'reviewQueue',
      'quizAttempts',
      'reciteAttempts',
      'tadabburEntries',
    ];

    const deleteQueryInBatches = async (q) => {
      const snap = await getDocs(q);
      if (snap.empty) return;
      const docs = snap.docs;
      for (let i = 0; i < docs.length; i += 450) {
        const batch = writeBatch(db);
        docs.slice(i, i + 450).forEach((d) => batch.delete(d.ref));
        // eslint-disable-next-line no-await-in-loop
        await batch.commit();
      }
    };

    for (const name of RELATED_COLLECTIONS) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await deleteQueryInBatches(
          query(collection(db, name), where('studentId', '==', studentId))
        );
      } catch (e) {
        console.warn(`permanentlyDeleteStudent: failed wiping ${name}:`, e.message);
      }
    }

    if (studentData?.studentUserId) {
      try {
        await deleteDoc(doc(db, 'users', studentData.studentUserId));
      } catch (e) {
        console.warn('permanentlyDeleteStudent: failed deleting student user doc:', e.message);
      }
    }

    if (studentData?.parentUserId && studentData?.fatherPhoneNumber && tid) {
      try {
        const siblingsQ = query(
          collection(db, 'students'),
          where('tenantId', '==', tid),
          where('fatherPhoneNumber', '==', studentData.fatherPhoneNumber),
        );
        const siblingsSnap = await getDocs(siblingsQ);
        const hasOtherSiblings = siblingsSnap.docs.some((d) => d.id !== studentId);
        if (!hasOtherSiblings) {
          await deleteDoc(doc(db, 'users', studentData.parentUserId));
        }
      } catch (e) {
        console.warn('permanentlyDeleteStudent: failed checking/deleting parent user doc:', e.message);
      }
    }

    try {
      await deleteDoc(doc(db, 'students', studentId));
    } catch (e) {
      throw new Error('Error permanently deleting student: ' + e.message);
    }
  }
};

// Phase 2.4 selector — switches at module load.
export const studentService = AUTH_BACKEND === 'api' ? studentServiceV2 : studentServiceFirebase;

// Daily Logs Service
export const dailyLogService = {
  async createLog(logData) {
    try {
      const docRef = await addDoc(collection(db, 'dailyLogs'), {
        ...logData,
        createdAt: Timestamp.now()
      });
      
      // Update student's memorized portions if it's new memorization
      if (logData.activityType === 'NEW') {
        await this.updateStudentMemorization(logData.studentId, logData.portionsAffected);
      }
      
      return docRef.id;
    } catch (error) {
      throw new Error('Error creating log: ' + error.message);
    }
  },

  async updateStudentMemorization(studentId, newPortions) {
    try {
      const student = await studentService.getStudentById(studentId);
      const currentPortions = student.memorizedPortions || [];
      const updatedPortions = [...new Set([...currentPortions, ...newPortions])];
      
      await studentService.updateStudent(studentId, {
        memorizedPortions: updatedPortions
      });
    } catch (error) {
      throw new Error('Error updating student memorization: ' + error.message);
    }
  },

  async getLogsByStudent(studentId, limit = 50) {
    try {
      const q = query(
        collection(db, 'dailyLogs'),
        where('studentId', '==', studentId),
        orderBy('date', 'desc'),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      throw new Error('Error fetching student logs: ' + error.message);
    }
  },

  async getLogsByDate(date, classId = null) {
    try {
      const dateStr = date.toISOString().split('T')[0];
      // Firestore rules require a tenantId filter on every list query.
      const tid = getCurrentTenantId();
      const clauses = [where('tenantId', '==', tid), where('date', '==', dateStr)];
      if (classId) clauses.push(where('classId', '==', classId));
      clauses.push(orderBy('createdAt', 'desc'));
      const q = query(collection(db, 'dailyLogs'), ...clauses);

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      throw new Error('Error fetching logs by date: ' + error.message);
    }
  },

  async getPreviousDayNewMemorization(studentId, currentDate) {
    try {
      const previousDate = new Date(currentDate);
      previousDate.setDate(previousDate.getDate() - 1);
      const prevDateStr = previousDate.toISOString().split('T')[0];
      
      const q = query(
        collection(db, 'dailyLogs'),
        where('studentId', '==', studentId),
        where('date', '==', prevDateStr),
        where('activityType', '==', 'NEW')
      );
      
      const querySnapshot = await getDocs(q);
      const logs = querySnapshot.docs.map(doc => doc.data());
      
      return logs.length > 0 ? logs[0].portionsAffected : [];
    } catch (error) {
      console.error('Error fetching previous day memorization:', error);
      return [];
    }
  }
};

// ----------------------------------------------------------------------------
// Sessions Service (Phase 2)
// ----------------------------------------------------------------------------
// Sessions are the Phase-2 replacement for the flat `dailyLogs` collection.
// A session captures ONE of three pedagogical acts: SABAK / SABAQ_PARA / DHOR,
// a set of affected portions (1..480), a 4-axis quality rating, and optional
// teacher notes. Historical `dailyLogs` documents are read via a shim so no
// migration is required for the UI to display past data.
// ----------------------------------------------------------------------------

const DEFAULT_QUALITY_RATING = {
  accuracy: null,
  fluency: null,
  tajweed: null,
  overall: null
};

const computeOverallFromAxes = ({ accuracy, fluency, tajweed }) => {
  const vals = [accuracy, fluency, tajweed].filter(
    (v) => typeof v === 'number' && !Number.isNaN(v)
  );
  if (vals.length === 0) return null;
  const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
  return Math.round(avg * 10) / 10;
};

const sessionServiceFirebase = {
  /**
   * Normalise a quality rating object. Accepts partial input and fills in
   * `overall` automatically when only the three sub-axes are provided.
   */
  normalizeQualityRating(rating) {
    const next = { ...DEFAULT_QUALITY_RATING, ...(rating || {}) };
    if (next.overall === null || next.overall === undefined) {
      next.overall = computeOverallFromAxes(next);
    }
    return next;
  },

  async createSession(sessionData) {
    try {
      const quality = sessionServiceFirebase.normalizeQualityRating(sessionData.qualityRating);
      const tid = sessionData.tenantId || getCurrentTenantId();
      if (!tid) throw new Error('createSession: missing tenantId');

      // Phase 9.2 — coherence validation. Hydrate the student + history once,
      // run the pure validator, refuse the write with a friendly Arabic
      // error if it returns ok:false.
      const portionsAffected = Array.isArray(sessionData.portionsAffected)
        ? sessionData.portionsAffected
        : [];
      if (portionsAffected.length > 0 && sessionData.studentId && sessionData.type) {
        const student = await studentServiceFirebase.getStudentById(sessionData.studentId);
        const [pastSessions, pastWirds] = await Promise.all([
          sessionServiceFirebase.getSessionsByStudent(sessionData.studentId, 1000).catch(() => []),
          listWirdRatingsByStudent(sessionData.studentId, { max: 2000 }).catch(() => [])
        ]);
        const sabakPortions = [];
        for (const s of pastSessions || []) {
          if (s.type === SESSION_TYPES.SABAK && Array.isArray(s.portionsAffected)) {
            for (const p of s.portionsAffected) sabakPortions.push(Number(p));
          }
        }
        for (const w of pastWirds || []) {
          if (w.kind === 'new' && w.portion && w.portion.mode === 'rubuu') {
            const portionId = (Number(w.portion.hizb) - 1) * 4 + Number(w.portion.rubuu);
            if (Number.isFinite(portionId)) sabakPortions.push(portionId);
          }
        }
        const result = validateWirdSave({
          student,
          sessionType: sessionData.type,
          portionsAffected,
          history: { sabakPortions, priorMemorizationEdits: student?.priorMemorizedPortions || [] }
        });
        if (!result.ok) {
          const err = new Error(result.errors[0]?.reason || 'Wird coherence rules violated');
          err.code = 'WIRD_COHERENCE_VIOLATION';
          err.errors = result.errors;
          throw err;
        }
      }
      // The firestore rule `allow create` demands:
      //   incomingSameTenant()  → payload.tenantId === userTenant()
      //   teacherId === request.auth.uid
      // Both must be in the payload at addDoc time or the write is
      // rejected with "Missing or insufficient permissions".
      const teacherId = sessionData.teacherId
        || auth?.currentUser?.uid
        || null;
      if (!teacherId) throw new Error('createSession: missing teacherId');
      const payload = {
        studentId: sessionData.studentId,
        classId: sessionData.classId || null,
        teacherId,
        tenantId: tid,
        date: sessionData.date,
        type: sessionData.type,
        portionsAffected: sessionData.portionsAffected || [],
        // Optional per-rub verse ranges when the teacher used the
        // "مخصص" picker to select partial rubuus (DailyProgress §3.2).
        portionVerses: sessionData.portionVerses || {},
        qualityRating: quality,
        teacherNotes: sessionData.teacherNotes || '',
        attendance: sessionData.attendance || 'present',
        mistakes: sessionData.mistakes || [],
        tajweedTags: Array.isArray(sessionData.tajweedTags) ? sessionData.tajweedTags : [],
        tajweedRemarks: Array.isArray(sessionData.tajweedRemarks) ? sessionData.tajweedRemarks : [],
        audioUrl: sessionData.audioUrl || null,
        notifiedParentAt: null,
        createdAt: Timestamp.now()
      };

      const docRef = await addDoc(collection(db, 'sessions'), payload);

      // If this is a SABAK (new memorisation) session, promote the portions
      // to the student's memorisedPortions roster.
      if (payload.type === SESSION_TYPES.SABAK && payload.portionsAffected.length > 0) {
        await sessionServiceFirebase._updateStudentMemorization(
          payload.studentId,
          payload.portionsAffected
        );
      }

      // Mirror to wirdRatings — Merouani1.md §1.1 made wirdRatings the
      // canonical per-portion source (consumed by Dashboard, SijillAlAwrad,
      // hafiz-rules detection). The seed mirrors wird→session for the
      // sessions tab; here we mirror session→wirds so live teacher saves
      // appear in the same downstream views without a parallel writer.
      // Fire-and-forget: a mirror hiccup must not fail the session save.
      sessionServiceFirebase._mirrorToWirdRatings(payload).catch((e) => {
        // eslint-disable-next-line no-console
        console.warn('wirdRatings mirror failed:', e?.message || e);
      });

      return docRef.id;
    } catch (error) {
      throw new Error('Error creating session: ' + error.message);
    }
  },

  async _updateStudentMemorization(studentId, newPortions) {
    const student = await studentService.getStudentById(studentId);
    const currentPortions = student.memorizedPortions || [];
    const updatedPortions = [...new Set([...currentPortions, ...newPortions])];
    await studentService.updateStudent(studentId, {
      memorizedPortions: updatedPortions
    });
  },

  // Map session payload → one wirdRating per affected portion, written in
  // parallel. Bypasses wirdRatingsService.saveWirdRating to avoid the
  // per-doc memorization-suggestion fan-out (the session path already
  // owns auto-promotion via _updateStudentMemorization).
  async _mirrorToWirdRatings(sessionPayload) {
    const portions = sessionPayload.portionsAffected || [];
    if (portions.length === 0) return;
    const kindByType = { SABAK: 'new', DHOR: 'revision', SABAQ_PARA: 'custom' };
    const kind = kindByType[sessionPayload.type];
    if (!kind) return;
    const q = sessionPayload.qualityRating || {};
    const overall = typeof q.overall === 'number' ? q.overall : 3;
    const score = (axis) => Math.round((typeof q[axis] === 'number' ? q[axis] : overall) * 20);
    const hifzScore = score('accuracy');
    const tajweedScore = score('tajweed');
    const tajweedRemarks = Array.isArray(sessionPayload.tajweedRemarks)
      ? sessionPayload.tajweedRemarks
      : [];
    const portionVerses = sessionPayload.portionVerses || {};
    const writes = portions.map((portionId) => {
      const hizb = Math.floor((portionId - 1) / 4) + 1;
      const rubuu = ((portionId - 1) % 4) + 1;
      const verses = portionVerses[portionId] || portionVerses[String(portionId)] || [];
      const mode = Array.isArray(verses) && verses.length > 0 ? 'custom' : 'rubuu';
      return addDoc(collection(db, 'wirdRatings'), {
        studentId: sessionPayload.studentId,
        classId: sessionPayload.classId || null,
        teacherId: sessionPayload.teacherId,
        tenantId: sessionPayload.tenantId,
        date: sessionPayload.date,
        kind,
        portion: { mode, hizb, rubuu, verses },
        hifzScore,
        tajweedScore,
        tajweedRemarks,
        audioNoteUrl: sessionPayload.audioUrl || null,
        audioDurationMs: null,
        notes: sessionPayload.teacherNotes || '',
        savedAt: Timestamp.now(),
      });
    });
    await Promise.all(writes);
  },

  async rateSession(sessionId, qualityRating) {
    try {
      const quality = sessionServiceFirebase.normalizeQualityRating(qualityRating);
      await updateDoc(doc(db, 'sessions', sessionId), {
        qualityRating: quality,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      throw new Error('Error rating session: ' + error.message);
    }
  },

  async updateSession(sessionId, patch) {
    try {
      const updates = { ...patch, updatedAt: Timestamp.now() };
      if (patch.qualityRating) {
        updates.qualityRating = sessionServiceFirebase.normalizeQualityRating(patch.qualityRating);
      }
      await updateDoc(doc(db, 'sessions', sessionId), updates);
    } catch (error) {
      throw new Error('Error updating session: ' + error.message);
    }
  },

  async deleteSession(sessionId) {
    try {
      await deleteDoc(doc(db, 'sessions', sessionId));
    } catch (error) {
      throw new Error('Error deleting session: ' + error.message);
    }
  },

  /**
   * Fetch sessions for one student, most recent first. Falls back to
   * legacy `dailyLogs` when the new `sessions` collection is empty so that
   * pre-migration data still surfaces in the UI.
   */
  async getSessionsByStudent(studentId, max = 100) {
    try {
      // tenantId filter is mandatory: rules deny list reads on `sessions`
      // unless the query is constrained to the caller's tenant (the rule
      // depends on resource.data.tenantId, which Firestore can only
      // statically verify when present in the query).
      const tid = getCurrentTenantId();
      const q = query(
        collection(db, 'sessions'),
        where('tenantId', '==', tid),
        where('studentId', '==', studentId),
        orderBy('date', 'desc'),
        orderBy('createdAt', 'desc'),
        fsLimit(max)
      );
      const snap = await getDocs(q);
      const sessions = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      if (sessions.length > 0) return sessions;

      // Fallback: read historical dailyLogs and project them into the new shape
      const legacy = await dailyLogService.getLogsByStudent(studentId);
      return legacy.map((log) => sessionServiceFirebase._legacyLogToSession(log));
    } catch (error) {
      throw new Error('Error fetching sessions for student: ' + error.message);
    }
  },

  async getSessionsByDate(date, classId = null) {
    // Must match how createSession writes `date` — which uses
    // dayjs().format('YYYY-MM-DD') in local time. toISOString() here
    // would shift to UTC and miss sessions in any non-UTC timezone
    // (e.g. Africa/Casablanca, UTC+1), making the day's wirds invisible.
    const pad = (n) => String(n).padStart(2, '0');
    const dateStr =
      typeof date === 'string'
        ? date
        : `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

    // Firestore rules require every list query to be constrained by
    // tenantId (sameTenant() is evaluated on resource.data, so the query
    // must pre-filter or the whole call is rejected — see tenantContext.js).
    const tid = getCurrentTenantId();

    // Each collection is queried independently so a missing composite
    // index on one does not kill the whole call.
    let sessions = [];
    try {
      const clauses = [where('tenantId', '==', tid), where('date', '==', dateStr)];
      if (classId) clauses.push(where('classId', '==', classId));
      clauses.push(orderBy('createdAt', 'desc'));
      const q = query(collection(db, 'sessions'), ...clauses);
      const snap = await getDocs(q);
      sessions = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (err) {
      console.warn('sessions query failed (likely missing index):', err.message);
    }

    let legacyLogs = [];
    try {
      legacyLogs = await dailyLogService.getLogsByDate(new Date(dateStr), classId);
    } catch (err) {
      console.warn('legacy dailyLogs query failed:', err.message);
    }

    return [
      ...sessions,
      ...legacyLogs.map((log) => sessionServiceFirebase._legacyLogToSession(log)),
    ];
  },

  /**
   * Find the most recent SABAK session for a student, regardless of day.
   * Used by the "Yesterday's Sabak" shortcut for SABAQ_PARA.
   */
  async getLatestSabakBefore(studentId, beforeDate) {
    try {
      const beforeStr =
        typeof beforeDate === 'string'
          ? beforeDate
          : beforeDate.toISOString().split('T')[0];

      // New collection first. tenantId filter required by rules.
      const tid = getCurrentTenantId();
      const q = query(
        collection(db, 'sessions'),
        where('tenantId', '==', tid),
        where('studentId', '==', studentId),
        where('type', '==', SESSION_TYPES.SABAK),
        where('date', '<', beforeStr),
        orderBy('date', 'desc'),
        fsLimit(1)
      );
      const snap = await getDocs(q);
      if (!snap.empty) {
        const d = snap.docs[0];
        return { id: d.id, ...d.data() };
      }

      // Fallback: legacy dailyLogs with activityType === 'NEW'
      const legacyQ = query(
        collection(db, 'dailyLogs'),
        where('studentId', '==', studentId),
        where('activityType', '==', 'NEW'),
        where('date', '<', beforeStr),
        orderBy('date', 'desc'),
        fsLimit(1)
      );
      const legacySnap = await getDocs(legacyQ);
      if (!legacySnap.empty) {
        return sessionServiceFirebase._legacyLogToSession({
          id: legacySnap.docs[0].id,
          ...legacySnap.docs[0].data()
        });
      }
      return null;
    } catch (error) {
      // Firestore will throw if composite index is missing; fall back gracefully
      console.warn('getLatestSabakBefore fallback:', error.message);
      return null;
    }
  },

  /**
   * Return a list of recent DHOR session quality ratings for a student,
   * ordered oldest -> newest. Used by the red-flag detector.
   */
  async getRecentDhorQualities(studentId, count = 3) {
    try {
      const tid = getCurrentTenantId();
      const q = query(
        collection(db, 'sessions'),
        where('tenantId', '==', tid),
        where('studentId', '==', studentId),
        where('type', '==', SESSION_TYPES.DHOR),
        orderBy('date', 'desc'),
        orderBy('createdAt', 'desc'),
        fsLimit(count)
      );
      const snap = await getDocs(q);
      const ratings = snap.docs
        .map((d) => d.data())
        .map((data) => data?.qualityRating?.overall)
        .filter((v) => typeof v === 'number');
      return ratings.reverse();
    } catch (error) {
      console.warn('getRecentDhorQualities failed:', error.message);
      return [];
    }
  },

  /**
   * Tenant-wide session fetch over a date window. Used by the Director
   * dashboard so the moudir sees activity across every class. Filtered
   * by tenantId at the query level so the rules engine accepts the read.
   *
   * @param {Object}  opts
   * @param {string}  opts.fromDate — 'YYYY-MM-DD' (inclusive)
   * @param {string}  opts.toDate   — 'YYYY-MM-DD' (inclusive)
   */
  async getSessionsForTenant({ fromDate, toDate } = {}) {
    const tid = getCurrentTenantId();
    if (!tid) return [];
    const from = typeof fromDate === 'string'
      ? fromDate
      : fromDate?.toISOString?.().split('T')[0];
    const to = typeof toDate === 'string'
      ? toDate
      : toDate?.toISOString?.().split('T')[0];
    if (!from || !to) return [];
    try {
      const q = query(
        collection(db, 'sessions'),
        where('tenantId', '==', tid),
        where('date', '>=', from),
        where('date', '<=', to),
        orderBy('date', 'asc'),
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
      console.warn('getSessionsForTenant failed:', error.message);
      return [];
    }
  },

  /**
   * Fetch all sessions for a student between two dates (inclusive), oldest
   * first, for the quality-trend chart on the dashboard.
   */
  async getQualityTrend(studentId, fromDate, toDate) {
    try {
      const fromStr =
        typeof fromDate === 'string' ? fromDate : fromDate.toISOString().split('T')[0];
      const toStr =
        typeof toDate === 'string' ? toDate : toDate.toISOString().split('T')[0];

      const tid = getCurrentTenantId();
      const q = query(
        collection(db, 'sessions'),
        where('tenantId', '==', tid),
        where('studentId', '==', studentId),
        where('date', '>=', fromStr),
        where('date', '<=', toStr),
        orderBy('date', 'asc')
      );
      const snap = await getDocs(q);
      const trend = snap.docs
        .map((d) => {
          const data = d.data();
          return {
            date: data.date,
            type: data.type,
            overall: data?.qualityRating?.overall ?? null
          };
        })
        .filter((row) => row.overall !== null);
      return trend;
    } catch (error) {
      console.warn('getQualityTrend failed:', error.message);
      return [];
    }
  },

  /**
   * Project a legacy dailyLogs document into the Phase-2 session shape so the
   * UI can consume both seamlessly during the soft migration window.
   */
  _legacyLogToSession(log) {
    return {
      id: log.id,
      _legacy: true,
      studentId: log.studentId,
      classId: log.classId,
      date: log.date,
      type: mapLegacyActivityType(log.activityType),
      portionsAffected: log.portionsAffected || [],
      qualityRating: { ...DEFAULT_QUALITY_RATING },
      teacherNotes: log.notes || '',
      attendance: 'present',
      createdAt: log.createdAt
    };
  }
};

// Phase 2.5 selector — switches at module load.
export const sessionService = AUTH_BACKEND === 'api' ? sessionServiceV2 : sessionServiceFirebase;

/**
 * Standalone helper: given the result of `getRecentDhorQualities`, returns
 * `true` if the student's DHOR quality has been below `threshold` for
 * `minCount` consecutive sessions. Pure function, easy to unit-test.
 */
export const isQualityDropAlert = (recentOveralls, threshold = 3, minCount = 3) => {
  if (!Array.isArray(recentOveralls)) return false;
  if (recentOveralls.length < minCount) return false;
  const lastN = recentOveralls.slice(-minCount);
  return lastN.every((v) => typeof v === 'number' && v < threshold);
};

// SMS Service (placeholder - integrate with actual SMS provider)
export const smsService = {
  async sendProgressSMS(phoneNumber, studentName, fatherName, newPortion, revisedPortion, mosqueName) {
    try {
      // This is a placeholder. In a real implementation, integrate with Twilio, Vonage, etc.
      const message = `السلام عليكم، الأستاذ ${fatherName}. اليوم، ${studentName} حفظ بنجاح ${newPortion} وراجع ${revisedPortion}. للغد، مراجعته ${revisedPortion}. - ${mosqueName}`;

      console.log(`SMS would be sent to ${phoneNumber}: ${message}`);

      // Simulate API call
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({ success: true, message: 'SMS sent successfully' });
        }, 1000);
      });
    } catch (error) {
      throw new Error('Error sending SMS: ' + error.message);
    }
  }
};

// ----------------------------------------------------------------------------
// Attendance Service (Phase 5)
// ----------------------------------------------------------------------------
// Lightweight per-day, per-student attendance records stored in the
// `attendance` collection. Document id is deterministic
// (`${date}_${studentId}`) so repeated writes for the same student/day
// overwrite cleanly and bulk-saves from the teacher UI stay idempotent.
//
// Statuses mirror what UIs in the Ilmify / QuranRecord space use so that
// future exports line up without a mapping step.
// ----------------------------------------------------------------------------

export const ATTENDANCE_STATUSES = {
  PRESENT: 'present',
  LATE: 'late',
  ABSENT: 'absent',
  EXCUSED: 'excused'
};

const isValidAttendanceStatus = (s) =>
  Object.values(ATTENDANCE_STATUSES).includes(s);

const toDateStr = (d) =>
  typeof d === 'string' ? d : d.toISOString().split('T')[0];

const attendanceServiceFirebase = {
  /**
   * Upsert a single attendance row. Safe to call repeatedly — the
   * deterministic doc id means teachers can flip a student from
   * "absent" to "present" without leaving orphaned duplicates.
   */
  async setAttendance({
    studentId,
    classId,
    date,
    status,
    notes = '',
    lateMinutes = null,
    reasonText = '',
    reasonAudioUrl = null,
    tenantId = null
  }) {
    if (!studentId) throw new Error('setAttendance: studentId is required');
    if (!isValidAttendanceStatus(status)) {
      throw new Error(`setAttendance: invalid status "${status}"`);
    }
    const dateStr = toDateStr(date);
    const docId = `${dateStr}_${studentId}`;
    const ref = doc(db, 'attendance', docId);
    await setDoc(
      ref,
      {
        studentId,
        classId: classId || null,
        date: dateStr,
        status,
        notes,
        lateMinutes:
          status === ATTENDANCE_STATUSES.LATE && Number.isFinite(Number(lateMinutes))
            ? Number(lateMinutes)
            : null,
        reasonText: reasonText || '',
        reasonAudioUrl: reasonAudioUrl || null,
        tenantId: tenantId || null,
        updatedAt: Timestamp.now()
      },
      { merge: true }
    );
    return docId;
  },

  /**
   * Bulk-save attendance for a whole class on a given day in one round
   * trip. Accepts an array of `{ studentId, status, notes? }` and reuses
   * a Firestore batch so the teacher's "Save all" click either lands
   * completely or not at all. When `actor` is provided, an immutable
   * entry is appended to `attendance_audit` so edit-after-save keeps a
   * traceable trail (per Merouani1 §2).
   */
  async bulkSetAttendance({
    classId,
    date,
    entries = [],
    tenantId = null,
    actor = null,
    previous = null
  }) {
    if (!Array.isArray(entries) || entries.length === 0) return 0;
    const dateStr = toDateStr(date);
    const batch = writeBatch(db);
    let count = 0;
    const kept = [];
    for (const entry of entries) {
      if (!entry?.studentId || !isValidAttendanceStatus(entry.status)) continue;
      const ref = doc(db, 'attendance', `${dateStr}_${entry.studentId}`);
      const lateMinutes =
        entry.status === ATTENDANCE_STATUSES.LATE && Number.isFinite(Number(entry.lateMinutes))
          ? Number(entry.lateMinutes)
          : null;
      const reasonText = entry.reasonText || '';
      const reasonAudioUrl = entry.reasonAudioUrl || null;
      batch.set(
        ref,
        {
          studentId: entry.studentId,
          classId: classId || null,
          date: dateStr,
          status: entry.status,
          notes: entry.notes || '',
          lateMinutes,
          reasonText,
          reasonAudioUrl,
          tenantId: tenantId || null,
          updatedAt: Timestamp.now(),
          updatedBy: actor?.uid || null
        },
        { merge: true }
      );
      kept.push({
        studentId: entry.studentId,
        status: entry.status,
        notes: entry.notes || '',
        lateMinutes,
        reasonText,
        reasonAudioUrl,
        previousStatus: previous?.[entry.studentId]?.status || null
      });
      count += 1;
    }
    if (count > 0) {
      await batch.commit();
      try {
        await addDoc(collection(db, 'attendance_audit'), {
          classId: classId || null,
          date: dateStr,
          tenantId: tenantId || null,
          savedAt: Timestamp.now(),
          savedBy: actor?.uid || null,
          savedByName: actor?.displayName || null,
          entries: kept
        });
      } catch (auditError) {
        console.warn('attendance_audit write failed:', auditError.message);
      }
    }
    return count;
  },

  /**
   * Read the audit trail for a given class+day. Used by the admin audit
   * drawer so teachers can see who flipped a mark and when.
   */
  async getAttendanceAudit({ classId, date }) {
    const dateStr = toDateStr(date);
    try {
      const q = query(
        collection(db, 'attendance_audit'),
        where('classId', '==', classId),
        where('date', '==', dateStr),
        orderBy('savedAt', 'desc')
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
      console.warn('getAttendanceAudit failed:', error.message);
      return [];
    }
  },

  /**
   * Fetch a class's attendance for one day. Returned as a map so the UI
   * can render the class roster with existing marks pre-filled.
   */
  async getAttendanceForDay({ classId, date }) {
    const dateStr = toDateStr(date);
    let q;
    if (classId) {
      q = query(
        collection(db, 'attendance'),
        where('classId', '==', classId),
        where('date', '==', dateStr)
      );
    } else {
      q = query(collection(db, 'attendance'), where('date', '==', dateStr));
    }
    try {
      const snap = await getDocs(q);
      const map = {};
      snap.forEach((d) => {
        const data = d.data();
        map[data.studentId] = { id: d.id, ...data };
      });
      return map;
    } catch (error) {
      console.warn('getAttendanceForDay failed:', error.message);
      return {};
    }
  },

  /**
   * Read attendance rows for a known roster on a given day by
   * fetching each `{date}_{studentId}` document directly.
   *
   * Why: the query-based `getAttendanceForDay` relies on Firestore's
   * zigzag merge across two equality filters AND on the read rule
   * `sameTenant(resource.data)` passing for every matched doc. If the
   * doc's tenantId drifts from the caller's custom-claim tenant (e.g.
   * stale claim after a re-seed, or docs written before custom claims
   * were set), the query silently returns an empty set — looks like
   * "the data vanished". Going direct-by-id makes a per-doc permission
   * failure an explicit error we can surface, AND bypasses any index
   * / query-plan surprises.
   */
  async getAttendanceByStudentIds({ date, studentIds }) {
    const dateStr = toDateStr(date);
    if (!Array.isArray(studentIds) || studentIds.length === 0) return {};
    const pairs = await Promise.all(
      studentIds.map(async (studentId) => {
        const ref = doc(db, 'attendance', `${dateStr}_${studentId}`);
        try {
          const snap = await getDoc(ref);
          if (snap.exists()) return [studentId, { id: snap.id, ...snap.data() }];
          return null;
        } catch (error) {
          // Surface permission-denied etc. so the caller can decide
          // whether to retry, warn, or re-auth. We don't swallow.
          console.error(
            `attendance read for ${studentId} on ${dateStr} failed:`,
            error.code,
            error.message,
          );
          throw error;
        }
      }),
    );
    const map = {};
    for (const p of pairs) if (p) map[p[0]] = p[1];
    return map;
  },

  /**
   * Tenant-wide attendance pull over a date window. Used by the
   * Director dashboard. The query is filtered by tenantId so it stays
   * inside the rules envelope.
   */
  async getAttendanceForTenant({ fromDate, toDate } = {}) {
    const tid = getCurrentTenantId();
    if (!tid) return [];
    const from = typeof fromDate === 'string'
      ? fromDate
      : fromDate?.toISOString?.().split('T')[0];
    const to = typeof toDate === 'string'
      ? toDate
      : toDate?.toISOString?.().split('T')[0];
    if (!from || !to) return [];
    try {
      // Uses the (tenantId ASC, date DESC) composite index that ships
      // with firestore.indexes.json. We sort client-side when ASC is
      // needed; the dataset is one tenant × ~30 days, so it's cheap.
      const q = query(
        collection(db, 'attendance'),
        where('tenantId', '==', tid),
        where('date', '>=', from),
        where('date', '<=', to),
        orderBy('date', 'desc'),
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
      console.warn('getAttendanceForTenant failed:', error.message);
      return [];
    }
  },

  /**
   * Fetch attendance rows for a single student across a date range.
   * Used by the parent portal and the monthly summary card.
   */
  async getAttendanceForStudent(studentId, fromDate, toDate) {
    if (!studentId) return [];
    const fromStr = toDateStr(fromDate);
    const toStr = toDateStr(toDate);
    try {
      const q = query(
        collection(db, 'attendance'),
        where('studentId', '==', studentId),
        where('date', '>=', fromStr),
        where('date', '<=', toStr),
        orderBy('date', 'asc')
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
      console.warn('getAttendanceForStudent failed:', error.message);
      return [];
    }
  }
};

// Phase 2.16 selector — switches at module load (mirrors sessionService).
export const attendanceService =
  AUTH_BACKEND === 'api' ? attendanceServiceV2 : attendanceServiceFirebase;

/**
 * Pure helper: compute summary counts from a list of attendance rows.
 * Exported so the Dashboard / Parent portal can reuse the same math.
 */
export const summarizeAttendance = (rows = []) => {
  const summary = {
    total: rows.length,
    present: 0,
    late: 0,
    absent: 0,
    excused: 0
  };
  for (const row of rows) {
    if (summary[row?.status] !== undefined) summary[row.status] += 1;
  }
  summary.attendanceRate =
    summary.total > 0
      ? Math.round(((summary.present + summary.late) / summary.total) * 100)
      : null;
  return summary;
};

// ----------------------------------------------------------------------------
// Curriculum Service (Phase 5)
// ----------------------------------------------------------------------------
// A curriculum plan sets a school-wide or per-class target for how much
// Quran a student is expected to memorize in a given time window. The
// Dashboard and the parent portal both use it to show "on track" vs
// "behind schedule" badges without hand-wavy heuristics.
//
// The simplest useful plan is: `targetKhatmMonths` (how long to finish
// the Mushaf) plus `dailyNewQuota` (how many rubs to learn per school
// day). Together they let us compute an expected cumulative total at
// any date and compare it against the student's actual `memorizedPortions`.
// ----------------------------------------------------------------------------

const TOTAL_RUB = 240; // 60 hizb * 4 rub = full Mushaf
const CURRICULUM_COLLECTION = 'curriculumPlans';

/**
 * Pure helper: compute how many rubs the plan expects as "done" by
 * `asOfDate`, given the start date and daily-new quota. Weekends are
 * intentionally counted as school days here — madaris typically hold
 * a weekly review cycle instead of days off, and the quota itself
 * already bakes in that rhythm. Capped at 240.
 */
export const computeExpectedProgress = ({
  startDate,
  dailyNewQuota,
  asOfDate = new Date()
}) => {
  if (!startDate || !dailyNewQuota || dailyNewQuota <= 0) return 0;
  const start = startDate instanceof Date ? startDate : new Date(startDate);
  const asOf = asOfDate instanceof Date ? asOfDate : new Date(asOfDate);
  const msPerDay = 1000 * 60 * 60 * 24;
  const days = Math.max(0, Math.floor((asOf - start) / msPerDay));
  return Math.min(TOTAL_RUB, Math.round(days * dailyNewQuota));
};

/**
 * Pure helper: translate raw plan + memorised-count into a UI-ready
 * status ("onTrack" | "behind" | "ahead" | "unknown") and a percent.
 */
export const computeCurriculumStatus = ({
  plan,
  memorizedCount = 0,
  asOfDate = new Date()
}) => {
  if (!plan || !plan.startDate || !plan.dailyNewQuota) {
    return { status: 'unknown', expected: 0, memorized: memorizedCount, delta: 0 };
  }
  const expected = computeExpectedProgress({
    startDate: plan.startDate,
    dailyNewQuota: plan.dailyNewQuota,
    asOfDate
  });
  const delta = memorizedCount - expected;
  let status = 'onTrack';
  // A ±1 day tolerance so a single missed session doesn't trigger an alert.
  if (delta < -Math.max(1, plan.dailyNewQuota)) status = 'behind';
  else if (delta > Math.max(1, plan.dailyNewQuota)) status = 'ahead';
  return { status, expected, memorized: memorizedCount, delta };
};

export const curriculumService = {
  async createPlan(planData) {
    const payload = {
      name: planData.name || '',
      description: planData.description || '',
      targetKhatmMonths: Number(planData.targetKhatmMonths) || 0,
      dailyNewQuota: Number(planData.dailyNewQuota) || 0,
      weeklyDhorQuota: Number(planData.weeklyDhorQuota) || 0,
      startDate: planData.startDate
        ? Timestamp.fromDate(new Date(planData.startDate))
        : Timestamp.now(),
      classId: planData.classId || null,
      tenantId: planData.tenantId || null,
      milestones: Array.isArray(planData.milestones) ? planData.milestones : [],
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };
    const ref = await addDoc(collection(db, CURRICULUM_COLLECTION), payload);
    return ref.id;
  },

  async updatePlan(planId, patch) {
    if (!planId) throw new Error('updatePlan: planId is required');
    const updates = { ...patch, updatedAt: Timestamp.now() };
    if (patch.startDate) {
      updates.startDate = Timestamp.fromDate(new Date(patch.startDate));
    }
    await updateDoc(doc(db, CURRICULUM_COLLECTION, planId), updates);
  },

  async deletePlan(planId) {
    if (!planId) throw new Error('deletePlan: planId is required');
    await deleteDoc(doc(db, CURRICULUM_COLLECTION, planId));
  },

  async getPlans() {
    try {
      const snap = await getDocs(
        query(collection(db, CURRICULUM_COLLECTION), orderBy('createdAt', 'desc'))
      );
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
      console.warn('curriculumService.getPlans failed:', error.message);
      return [];
    }
  },

  async getPlanById(planId) {
    if (!planId) return null;
    try {
      const snap = await getDoc(doc(db, CURRICULUM_COLLECTION, planId));
      if (!snap.exists()) return null;
      return { id: snap.id, ...snap.data() };
    } catch (error) {
      console.warn('curriculumService.getPlanById failed:', error.message);
      return null;
    }
  },

  async getPlanForClass(classId) {
    if (!classId) return null;
    try {
      const q = query(
        collection(db, CURRICULUM_COLLECTION),
        where('classId', '==', classId),
        orderBy('createdAt', 'desc'),
        fsLimit(1)
      );
      const snap = await getDocs(q);
      if (snap.empty) return null;
      const d = snap.docs[0];
      return { id: d.id, ...d.data() };
    } catch (error) {
      console.warn('curriculumService.getPlanForClass failed:', error.message);
      return null;
    }
  },

  /**
   * Compute on-track / behind status for every student in a given
   * class, returning a map keyed by studentId. Memoisation-friendly:
   * the caller supplies pre-fetched students so the method itself
   * stays a pure orchestration step.
   */
  async getClassProgressReport({ classId, students = [] }) {
    const plan = await curriculumService.getPlanForClass(classId);
    const now = new Date();
    const report = {};
    for (const student of students) {
      const memorizedCount = (student.memorizedPortions || []).length;
      const startDate = plan?.startDate?.toDate
        ? plan.startDate.toDate()
        : plan?.startDate;
      const status = computeCurriculumStatus({
        plan: plan
          ? {
              startDate,
              dailyNewQuota: plan.dailyNewQuota
            }
          : null,
        memorizedCount,
        asOfDate: now
      });
      report[student.id] = {
        ...status,
        planId: plan?.id || null,
        planName: plan?.name || null
      };
    }
    return { plan, report };
  }
};